import { Banner } from "@dropbox/dig-components/banner";
import { Button } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { Box, Stack } from "@dropbox/dig-foundations";
import { PAP_Select_DashSearchResultSeeMore } from "@mirage/analytics/events/types/select_dash_search_results_see_more";
import { useResultPAPLogger } from "@mirage/shared/hooks/useResultPAPLogger";
import {
  concatActiveFilterBindings,
  contentTypeToSearchFilter,
} from "@mirage/shared/search/search-filters";
import i18n from "@mirage/translations";
import { reportEvent } from "@mirage/webapp/analytics/logging";
import classnames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CategoryImageGrid } from "./CategoryImageGrid";
import { CategoryResultList } from "./CategoryResultList";
import styles from "./CategorySearchAccordion.module.css";
import { useFilterSearch } from "./useFilterSearch";

// eslint-disable-next-line no-restricted-imports
import { SlackDark } from "@dropbox/dash-component-library/src/icons/SlackDark";
import { UIIcon } from "@dropbox/dig-icons";
import { AddLine } from "@dropbox/dig-icons/dist/mjs/assets";
import { useSlackPrivateConnectorBanner } from "@mirage/mosaics/SearchPage/CategorySearchAccordion/useSlackPrivateConnectorBanner";
import { answerRetrievalCategoryResultsAtom } from "@mirage/mosaics/SearchPageDataRetrieval/searchPageDataRetrieval";
import { LoadingResultList } from "@mirage/search/SearchResults";
import type { SearchResult } from "@mirage/service-dbx-api";
import { openURL } from "@mirage/service-platform-actions";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import { useAtom } from "jotai";
import type { CategoryConfig } from "./config";

type Props = {
  query: string;
  activeFilters?: SearchFilter[];
  seeAllForCategory: (filter: SearchFilter[]) => void;
  categories: CategoryConfig[];
  hasResults?: () => void;
  shouldSkipCache?: boolean;
  waitForAllResults?: boolean;
};

export function CategorySearchAccordion({
  query,
  seeAllForCategory,
  categories,
  activeFilters,
  hasResults,
  shouldSkipCache,
  waitForAllResults = false,
}: Props) {
  const [categoryHasResults, setCategoryHasResults] = useState<
    Record<string, boolean>
  >({});
  const [categoriesCompleted, setCategoriesCompleted] = useState<
    Record<string, boolean>
  >({});
  const [allCategoriesCompleted, setAllCategoriesCompleted] =
    useState<boolean>(!waitForAllResults);

  const categoryResultsReturned = (categoryId: string) => {
    setCategoryHasResults((prev) => ({
      ...prev,
      [categoryId]: true,
    }));
    hasResults?.();
  };

  const categoryReturned = (categoryId: string) => {
    setCategoriesCompleted((prev) => ({
      ...prev,
      [categoryId]: true,
    }));
  };

  useEffect(() => {
    if (waitForAllResults) {
      setAllCategoriesCompleted(
        Object.values(categoriesCompleted).length === categories.length,
      );
    }
  }, [categoriesCompleted]);

  useEffect(() => {
    if (query) {
      setCategoryHasResults({});
      setCategoriesCompleted({});
      setAllCategoriesCompleted(!waitForAllResults);
    }
  }, [query]);

  return (
    <Box marginBottom="24" width="100%">
      {waitForAllResults && !allCategoriesCompleted && (
        <LoadingResultList count={3} singleLine />
      )}
      {categories.map((category) => {
        return (
          <Category
            key={category.id}
            query={query}
            activeFilters={activeFilters}
            category={category}
            hasResults={() => categoryResultsReturned(category.id)}
            onComplete={() => categoryReturned(category.id)}
            seeAllForCategory={seeAllForCategory}
            shouldSkipCache={shouldSkipCache}
            categoryHasResults={
              allCategoriesCompleted && categoryHasResults[category.id]
            }
          />
        );
      })}
    </Box>
  );
}

type CategoryProps = {
  query: string;
  category: CategoryConfig;
  activeFilters?: SearchFilter[];
  seeAllForCategory: (filter: SearchFilter[]) => void;
  hasResults?: () => void;
  onComplete?: () => void;
  shouldSkipCache?: boolean;
  categoryHasResults?: boolean;
};

function Category({
  query,
  category,
  activeFilters,
  hasResults,
  onComplete,
  seeAllForCategory,
  shouldSkipCache,
  categoryHasResults,
}: CategoryProps) {
  const isMobileSize = useIsMobileSize();
  const filter = useMemo(
    () => contentTypeToSearchFilter(category.contentType) || null,
    [category.contentType],
  );
  const { loading, results } = useFilterSearch(
    query,
    filter,
    category.limit,
    shouldSkipCache,
  );
  const { logSearchResultOpen, logSearchResultShown } = useResultPAPLogger({
    isTypeahead: false,
  });
  const [, setCategoryResults] = useAtom(answerRetrievalCategoryResultsAtom);

  useEffect(() => {
    if (category.id !== "image" && results.length > 0) {
      hasResults?.();
    }
    setCategoryResults((prev) => ({
      ...prev,
      [category.id]: results,
    }));
  }, [results]);

  useEffect(() => {
    if (!loading && (results.length === 0 || category.id !== "image")) {
      onComplete?.();
    }
  }, [loading]);

  const onImageLoadSuccess = () => {
    if (!loading) {
      onComplete?.();
    }
    hasResults?.();
  };

  const handleResultClick = useCallback(
    (result: SearchResult) => {
      const filter = contentTypeToSearchFilter(category.contentType);
      logSearchResultOpen({
        defaultProps: {
          query,
          searchResult: result,
          filters: filter ? [filter] : [],
          results,
          featureLine: "search",
        },
        rightRailOpened: false,
        launchMethod: "click",
        actionSurfaceComponent: "result_row",
        categorizedResult: true,
      });
    },
    [logSearchResultOpen, query, category.contentType, results],
  );

  const handleResultShown = useCallback(
    (result: SearchResult) => {
      const filter = contentTypeToSearchFilter(category.contentType);
      logSearchResultShown(
        {
          query,
          searchResult: result,
          filters: filter ? [filter] : [],
          results,
          featureLine: "search",
        },
        "serp_categories",
      );
    },
    [logSearchResultShown, query, category.contentType, results],
  );

  const handleSeeAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!filter) return;
    seeAllForCategory([filter]);
    reportEvent(
      PAP_Select_DashSearchResultSeeMore({
        featureLine: "search",
        categorizedResult: true,
        activeFilters: concatActiveFilterBindings([filter]),
      }),
    );
  };

  return (
    <Box
      className={classnames(
        isMobileSize && styles.accordionContainerMobile,
        categoryHasResults ? styles.showCategory : styles.hideCategory,
      )}
      display="flex"
      flexDirection="column"
      key={category.id}
    >
      <div className={styles.accordionHeader}>
        <Box
          width="100%"
          display="flex"
          alignItems="center"
          borderWidth="1"
          justifyContent="space-between"
          paddingBottom="16"
        >
          <Text variant="label" size="medium" color="subtle">
            {category.label}
          </Text>
          <Box display="flex" alignItems="center" className={styles.gapSpacing}>
            <Button size="small" variant="transparent" onClick={handleSeeAll}>
              {i18n.t("see_all")}
            </Button>
          </Box>
        </Box>
      </div>
      {category.layout === "list" ? (
        <>
          {category.id === "messages" && <SlackBanner />}
          <CategoryResultList
            results={results}
            query={query}
            activeFilters={activeFilters}
            loading={loading}
            onResultClick={handleResultClick}
            onResultShown={handleResultShown}
            snippetLinesShown={category.snippetLinesShown}
          />
        </>
      ) : (
        <CategoryImageGrid
          results={results}
          loading={loading}
          onResultClick={handleResultClick}
          onResultShown={handleResultShown}
          onSuccessfulLoad={onImageLoadSuccess}
        />
      )}
    </Box>
  );
}

const SlackBanner = () => {
  const { slackBannerShown, dismissSlackBanner, handleConnect } =
    useSlackPrivateConnectorBanner();

  if (!slackBannerShown) return null;

  const handleLearnMoreClicked = () => {
    openURL(
      "https://help.dropbox.com/integrations/add-slack-to-dash#How-to-add-your-individual-accounts-to-Dash-for-private-content",
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      marginBottom="16"
      className={styles.listContainer}
    >
      <Banner
        onRequestClose={dismissSlackBanner}
        withCloseButton={i18n.t("close")}
        // TODO: We need a way to use DCL icons with DIG components without importing individual icon svgs
        //  Once that is fixed, we can use the DCL icon directly and remove disabled eslint rule on the import
        withLeftIcon={<UIIcon src={SlackDark} width="24px" height="24px" />}
        style={{
          paddingTop: "var(--dig-spacing__dimension__12)",
          paddingBottom: "var(--dig-spacing__dimension__12)",
        }}
      >
        <Stack
          gap={"Micro Small"}
          style={{
            minHeight: `calc(2 * var(--dig-spacing__dimension__12) + 40px)`,
          }}
        >
          <Stack gap={"Micro Small"}>
            <Text
              isBold
              tagName="p"
              style={{ marginTop: 0, marginRight: 0, marginLeft: 0 }}
            >
              {i18n.t("connect_private_account_banner_title")}
            </Text>
            <Text tagName="p" style={{ margin: 0 }}>
              {i18n.t("connect_private_account_banner_subtitle")}
            </Text>
          </Stack>
          <Banner.Actions
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              gap: "var(--dig-spacing__micro__large)",
              alignItems: "center",
            }}
          >
            <Button
              variant="outline"
              onClick={handleConnect}
              withIconStart={<UIIcon src={AddLine} />}
            >
              {i18n.t("connect_private_account_add")}
            </Button>
            <Button variant="transparent" onClick={handleLearnMoreClicked}>
              {i18n.t("connect_private_account_learn_more")}
            </Button>
          </Banner.Actions>
        </Stack>
      </Banner>
    </Box>
  );
};
